import React, { useMemo } from 'react';
import { useProducts } from '../useProducts';
import { useSiteSettings } from '../SiteSettingsContext';
import { heroActionsNeedCatalog, resolveStorefrontHeroActions } from '../storefrontHeroActions';
import GiftFinderLink from '../giftFinder/GiftFinderLink';
import HeroActionLink from './HeroActionLink';

/**
 * Primary + secondary hero buttons (admin-configured). Optional gift finder link.
 * @param {'home'|'shop'|'about'} placement
 */
export default function StorefrontHeroActions({
  placement,
  className = 'shop-hero-actions d-flex flex-column flex-sm-row justify-content-center align-items-center gap-3 mt-4',
  giftFinderPlacement = null,
  giftFinderClassName = 'btn btn-outline-secondary btn-lg',
  primaryBtnClass = 'btn btn-primary btn-lg',
  secondaryBtnClass = 'btn btn-outline-secondary btn-lg',
}) {
  const { settings: site, status: siteStatus } = useSiteSettings();
  const { categories, loading: catalogLoading } = useProducts();

  const needsCatalog = useMemo(
    () => heroActionsNeedCatalog(site, placement),
    [site, placement],
  );

  const actionsReady = siteStatus !== 'loading' && (!needsCatalog || !catalogLoading);

  const buttons = useMemo(() => {
    if (!actionsReady) return [];
    return resolveStorefrontHeroActions(site, categories, placement);
  }, [actionsReady, site, categories, placement]);

  if (!actionsReady) {
    return (
      <div
        className={`${className} shop-hero-actions--pending`.trim()}
        aria-busy="true"
        aria-label="Loading shop actions"
      />
    );
  }

  if (buttons.length === 0 && !giftFinderPlacement) {
    return null;
  }

  return (
    <div className={className}>
      {buttons.map((btn) => (
        <HeroActionLink
          key={btn.key}
          to={btn.path}
          className={btn.variant === 'primary' ? primaryBtnClass : secondaryBtnClass}
        >
          {placement === 'home' && btn.variant === 'primary' ? `🛍️ ${btn.label}` : btn.label}
        </HeroActionLink>
      ))}
      {giftFinderPlacement ? (
        <GiftFinderLink placement={giftFinderPlacement} className={giftFinderClassName} />
      ) : null}
    </div>
  );
}
